import { useTickets } from "@/hooks/useTickets";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { formatDateWithYear } from "@/utils/dateFormatters";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";
import { CreditCard, Ticket } from "lucide-react";
import CardSkeleton from "@/components/CardSkeleton";
import { Typography } from "@/components/ui/typography";
import TicketsStub from "@/components/TicketsStub";

const Tickets = () => {
  const { ticketsData, isLoading } = useTickets();

  const getAvailableSessions = (ticket: {
    type: string;
    service_list?: Array<{ count?: number | string; count_reserves?: number | string }>;
  }) => {
    if (ticket.type === "service" || !ticket.service_list?.length) {
      return 0;
    }

    return ticket.service_list.reduce((acc, item) => {
      return acc + (Number(item.count) || 0) + (Number(item.count_reserves) || 0);
    }, 0);
  };

  return (
    <div className="flex flex-col p-3">
      <BreadcrumbComponent link={"Расписание"} />
      <div className="mb-6 flex items-center gap-2">
        <Ticket className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-medium text-gray-900">Абонементы</h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-1">
        {!isLoading && (!ticketsData || ticketsData.length === 0) && <TicketsStub />}
        {isLoading
          ? [...Array(1)].map((_, index) => <CardSkeleton key={index} />)
          : ticketsData &&
            ticketsData.map((ticket) => {
              const availableSessions = getAvailableSessions(ticket);
              return (
                <Card
                  key={ticket.ticket_id}
                  className="w-[calc(100vw-1.5rem)] overflow-hidden transition-all hover:shadow-lg md:w-[auto]"
                >
                  <CardHeader className="border-b bg-muted/50 p-4">
                    <div className="flex flex-col gap-2">
                      <div className="group flex items-center gap-3">
                        <div className="rounded-full bg-primary/10 p-2 sm:p-2">
                          <CreditCard className="h-5 w-5 min-w-[20px] text-primary" />
                        </div>
                        <Typography
                          variant="h3"
                          className="text-lg font-normal text-gray-800 transition-colors group-hover:text-primary md:min-w-[20rem]"
                        >
                          {ticket.title}
                        </Typography>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between text-sm">
                        <Typography variant="muted" className="text-muted-foreground">
                          Дата активации:
                        </Typography>
                        <div>
                          <Typography variant="p" className="text-base leading-relaxed">
                            {formatDateWithYear(ticket.active_date)}
                          </Typography>
                        </div>
                      </div>

                      {ticket.end_date && (
                        <div className="flex items-center justify-between text-sm">
                          <Typography variant="muted" className="text-muted-foreground">
                            Дата окончания:
                          </Typography>
                          <Typography variant="p" className="text-base leading-relaxed">
                            {formatDateWithYear(ticket.end_date)}
                          </Typography>
                        </div>
                      )}
                    </div>

                    {/* Блок с филиалами
                    {ticket.available_clubs && (
                      <div className="flex flex-col gap-2 pt-3 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            Доступно для филиалов:
                          </span>
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            {ticket.available_clubs.length}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {ticket.available_clubs.map((club) => (
                            <div
                              key={club.title}
                              className="group flex items-center justify-end gap-2">
                              <span className="text-sm font-medium text-gray-600 group-hover:text-primary transition-colors">
                                {club.title}
                              </span>
                              <div className="h-1.5 w-1.5 rounded-full bg-primary/30 group-hover:bg-primary transition-colors" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )} */}

                    {ticket.type !== "service" && availableSessions > 0 && (
                      <div className="flex items-center justify-between border-t pt-3">
                        <span className="text-sm text-muted-foreground">Доступно занятий:</span>
                        <div className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1.5">
                          <span className="font-medium text-primary">{availableSessions}</span>
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                        </div>
                      </div>
                    )}

                    {ticket.type === "service" && ticket.count && (
                      <div className="flex items-center justify-between border-t pt-3">
                        <span className="text-sm text-muted-foreground">Количество:</span>
                        <div className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1.5">
                          <span className="font-medium text-primary">{ticket.count}</span>
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
      </div>
    </div>
  );
};

export default Tickets;
